/* ═══════════════════════════════════════════════════════════════
   PersonPanel — the Directory tab's master-detail RIGHT side.
   Replaces the old MemberEditor modal (pages/team/MemberEditor.tsx)
   with an inline panel per docs/PEOPLE_REBUILD_PLAN.md §5. Carries
   every field/behavior from the old modal, restructured:

   - Full Name → First / Middle / Last (People Rebuild grant #1).
   - "Active member" checkbox → status chip + Deactivate…/Reactivate
     flip button (grant #2). Deactivate still funnels through the
     page's ConfirmDialog (cascade warning); Reactivate is an
     immediate plain save({active:true}), same as before when the
     checkbox + Save was the only reactivation path.
   - Team Membership goes from READ-ONLY (old MemberEditor) to fully
     editable here — team assignment now happens FROM the person
     (People Rebuild grant #3): chips with leader/member toggle + X
     remove + a SearchableSelect that stays open for multi-add,
     reusing the exact TeamService calls the old TeamEditor used
     (wired by the parent).
   - Project Assignments section is unchanged from the old modal.
   ═══════════════════════════════════════════════════════════════ */

import { X, Star, Archive, ArchiveRestore, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';
import { SearchableSelect } from '../../components/SearchableSelect';
import { StatusBadge } from '../../components/StatusBadge';
import type {
  TeamMember,
  TeamAssignment,
  ProjectAssignment,
  Project,
  Role,
  BillingRole,
  Team,
} from '../../../shared/types';
import type { Department } from '../../../shared/entities';
import {
  sectionHeading,
  fieldBlock,
  fieldLabel,
  inputStyle,
  emptyPill,
  displayName,
} from './styles';

export interface PersonPanelProps {
  member: TeamMember;
  dirty: boolean;
  roles: Role[];
  billingRoles: BillingRole[];
  departments: Department[];
  teams: Team[];
  canSeeRates: boolean;
  canManageRoles: boolean;
  /** Whether ANY removal action is available on this member (deactivate or
   *  hard delete) — governs whether the lifecycle button shows at all. */
  canRemove: boolean;
  canHardDelete: boolean;
  canEditTeams: boolean;
  projects: Project[];
  memberTeamAssignments: TeamAssignment[];
  memberProjectAssignments: ProjectAssignment[];
  availableProjects: Project[];
  availableTeams: Team[];
  addingProject: boolean;
  onUpdate: (updates: Partial<TeamMember>) => void;
  onSave: () => void;
  onDeactivate: (id: string) => void;
  onReactivate: (id: string) => void;
  onHardDelete: (id: string) => void;
  onClose: () => void;
  onAssignProject: (projectId: string) => void;
  onUnassignProject: (assignmentId: string) => void;
  onToggleAddProject: () => void;
  onAddTeam: (teamId: string) => void;
  onRemoveTeam: (teamId: string) => void;
  onToggleTeamRole: (teamId: string) => void;
}

export function PersonPanel({
  member,
  dirty,
  roles,
  billingRoles,
  departments,
  teams,
  canSeeRates,
  canManageRoles,
  canRemove,
  canHardDelete,
  canEditTeams,
  projects,
  memberTeamAssignments,
  memberProjectAssignments,
  availableProjects,
  availableTeams,
  addingProject,
  onUpdate,
  onSave,
  onDeactivate,
  onReactivate,
  onHardDelete,
  onClose,
  onAssignProject,
  onUnassignProject,
  onToggleAddProject,
  onAddTeam,
  onRemoveTeam,
  onToggleTeamRole,
}: PersonPanelProps) {
  function projectById(id: string): Project | undefined {
    return projects.find((p) => p.id === id);
  }
  function teamById(id: string): Team | undefined {
    return teams.find((t) => t.id === id);
  }

  const headerName = displayName(member) || 'New Person';

  return (
    <div
      style={{
        width: rem(440),
        flexShrink: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid var(--bd)',
        background: 'var(--bg)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 'var(--space-2)',
          padding: 'var(--space-4) var(--space-4) var(--space-3)',
          borderBottom: '1px solid var(--bd)',
          flexShrink: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {headerName}
          </div>
          <div style={{ marginTop: rem(6) }}>
            <StatusBadge status={member.active ? 'active' : 'archived'} />
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={onClose}
          title="Close"
          aria-label="Close person panel"
        >
          <Icon icon={X} em={1.2308} />
        </Button>
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-4)' }}>
        <div style={sectionHeading}>Member Details</div>

        <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
          <label style={{ flex: 2 }}>
            <span style={fieldLabel}>First Name</span>
            <input
              value={member.firstName || ''}
              onChange={(e) => onUpdate({ firstName: e.target.value })}
              style={inputStyle}
              placeholder="e.g. Jane"
              autoFocus
            />
          </label>
          <label style={{ flex: 1.5 }}>
            <span style={fieldLabel}>Middle</span>
            <input
              value={member.middleName || ''}
              onChange={(e) => onUpdate({ middleName: e.target.value })}
              style={inputStyle}
              placeholder="optional"
            />
          </label>
          <label style={{ flex: 2 }}>
            <span style={fieldLabel}>Last Name</span>
            <input
              value={member.lastName || ''}
              onChange={(e) => onUpdate({ lastName: e.target.value })}
              style={inputStyle}
              placeholder="e.g. Smith"
            />
          </label>
        </div>

        <label style={fieldBlock}>
          <span style={fieldLabel}>Email</span>
          <input
            type="email"
            value={member.email || ''}
            onChange={(e) => onUpdate({ email: e.target.value })}
            style={inputStyle}
            placeholder="jane@example.com"
          />
        </label>

        <label style={fieldBlock}>
          <span style={fieldLabel}>Secondary Email</span>
          <input
            type="email"
            value={member.secondaryEmail || ''}
            onChange={(e) => onUpdate({ secondaryEmail: e.target.value })}
            style={inputStyle}
            placeholder="optional — e.g. after a name change"
          />
          <span
            style={{
              display: 'block',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--tm)',
              marginTop: 'var(--space-1)',
            }}
          >
            HAR sync and email matching accept either address.
          </span>
        </label>

        <label style={fieldBlock}>
          <span style={fieldLabel}>Privilege Role</span>
          <SearchableSelect
            value={member.roleId || ''}
            options={roles.map((r) => ({ value: r.id, label: r.name }))}
            onChange={(v) => {
              const roleId = v || undefined;
              const rn = roleId ? roles.find((r) => r.id === roleId)?.name || '' : '';
              onUpdate({ roleId, role: rn });
            }}
            placeholder="-- Select Role --"
            searchPlaceholder="Search roles…"
            clearable={false}
            disabled={!canManageRoles}
          />
          {!canManageRoles && (
            <span
              style={{
                display: 'block',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--tm)',
                marginTop: 'var(--space-1)',
              }}
            >
              Only members with role-management permission can change this.
            </span>
          )}
        </label>

        <label style={fieldBlock}>
          <span style={fieldLabel}>Title</span>
          <SearchableSelect
            value={member.billingRoleId || ''}
            options={billingRoles.map((br) => ({
              value: br.id,
              label: br.name,
              hint: canSeeRates && br.defaultRate != null ? `$${br.defaultRate}/hr` : undefined,
            }))}
            onChange={(v) => onUpdate({ billingRoleId: v || undefined })}
            placeholder="-- None --"
            searchPlaceholder="Search titles…"
          />
        </label>

        <label style={fieldBlock}>
          <span style={fieldLabel}>Department</span>
          <SearchableSelect
            value={member.departmentId || ''}
            options={departments.map((d) => ({
              value: d.id,
              label: d.abbreviation ? `${d.abbreviation} — ${d.name}` : d.name,
            }))}
            onChange={(v) => onUpdate({ departmentId: v || undefined })}
            placeholder="-- None --"
            searchPlaceholder="Search departments…"
          />
        </label>

        <label style={fieldBlock}>
          <span style={fieldLabel}>Employment Type</span>
          <div
            style={{
              display: 'flex',
              gap: 0,
              borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
              border: '1px solid var(--bd)',
            }}
          >
            {(
              [
                { value: 'salary', label: 'Salary', sub: '40h target' },
                { value: 'full_time_hourly', label: 'FT Hourly', sub: '40h target' },
                { value: 'part_time_hourly', label: 'Part-time', sub: '≤20h cap' },
              ] as const
            ).map((opt) => {
              const active = (member.employmentType || 'salary') === opt.value;
              return (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => {
                    const wantCap = opt.value === 'part_time_hourly' ? 20 : 40;
                    const next: Partial<TeamMember> = { employmentType: opt.value };
                    if (
                      member.weeklyCapacityHours == null ||
                      member.weeklyCapacityHours === 20 ||
                      member.weeklyCapacityHours === 40
                    ) {
                      next.weeklyCapacityHours = wantCap;
                    }
                    onUpdate(next);
                  }}
                  style={{
                    flex: 1,
                    padding: 'var(--space-2) var(--space-1)',
                    background: active ? 'var(--ac)' : 'var(--ib)',
                    color: active ? 'var(--color-text-inverse)' : 'var(--tx)',
                    border: 'none',
                    borderRight: '1px solid var(--bd)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'background .12s, color .12s',
                    lineHeight: 1.2,
                  }}
                >
                  <div>{opt.label}</div>
                  <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 400, opacity: 0.8 }}>
                    {opt.sub}
                  </div>
                </button>
              );
            })}
          </div>
        </label>

        <label style={fieldBlock}>
          <span style={fieldLabel}>Weekly Capacity (hours)</span>
          <input
            type="number"
            min="0"
            max="80"
            step="1"
            value={member.weeklyCapacityHours ?? 40}
            onChange={(e) => {
              const n = parseFloat(e.target.value);
              onUpdate({ weeklyCapacityHours: isFinite(n) ? n : undefined });
            }}
            style={{ ...inputStyle, width: rem(120), fontFamily: "'JetBrains Mono', monospace" }}
          />
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--tm)',
              marginTop: 'var(--space-1)',
            }}
          >
            Used by the forecast grid capacity bar — under-allocation goes red below this for
            full-timers, over-allocation goes red above for part-timers.
          </div>
        </label>

        {/* Save / lifecycle actions */}
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            flexWrap: 'wrap',
            marginTop: 'var(--space-4)',
            marginBottom: 'var(--space-5)',
          }}
        >
          <Button onClick={onSave}>{dirty ? 'Save •' : 'Save'}</Button>
          {member.id && canRemove && member.active && (
            <Button
              variant="destructive"
              className="gap-1.5"
              onClick={() => onDeactivate(member.id)}
              title="Archive — removes from all teams and the org chart"
            >
              <Icon icon={Archive} em={1.2727} aria-hidden="true" />
              Archive…
            </Button>
          )}
          {member.id && canRemove && !member.active && (
            <Button
              variant="secondary"
              className="gap-1.5"
              onClick={() => onReactivate(member.id)}
              title="Restore"
            >
              <Icon icon={ArchiveRestore} em={1.2727} aria-hidden="true" />
              Restore
            </Button>
          )}
          {member.id && canHardDelete && (
            <Button
              variant="destructive"
              className="gap-1.5"
              onClick={() => onHardDelete(member.id)}
              title="Delete permanently — cannot be undone"
            >
              <Icon icon={Trash2} em={1.2727} aria-hidden="true" />
              Delete permanently
            </Button>
          )}
        </div>

        {/* Teams */}
        <div style={{ marginBottom: rem(20) }}>
          <div style={sectionHeading}>Teams</div>
          {!member.id ? (
            <div style={emptyPill}>Save member first, then assign to teams.</div>
          ) : (
            <>
              {memberTeamAssignments.length === 0 ? (
                <div style={emptyPill}>Not assigned to any teams.</div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-1)',
                    marginBottom: 'var(--space-2)',
                  }}
                >
                  {memberTeamAssignments.map((ta) => {
                    const t = teamById(ta.teamId);
                    const tc = t?.color;
                    return (
                      <div
                        key={ta.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          padding: `${rem(6)} ${rem(10)}`,
                          background: tc ? tc + '14' : 'var(--sf)',
                          borderRadius: 'var(--radius-md)',
                          border: `1px solid ${tc ? tc + '40' : 'var(--bd)'}`,
                        }}
                      >
                        <span
                          aria-hidden="true"
                          style={{
                            width: 'var(--space-2)',
                            height: 'var(--space-2)',
                            borderRadius: '50%',
                            background: tc || 'var(--tm)',
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{ flex: 1, fontSize: 'var(--font-size-base)', fontWeight: 500 }}
                        >
                          {t?.name || '(deleted team)'}
                        </span>
                        {canEditTeams && (
                          <div
                            role="group"
                            aria-label={`${t?.name || 'Team'} role`}
                            style={{
                              display: 'inline-flex',
                              borderRadius: 'var(--radius-sm)',
                              border: '1px solid var(--bd)',
                              overflow: 'hidden',
                            }}
                          >
                            <button
                              type="button"
                              aria-pressed={ta.teamRole === 'leader'}
                              title={
                                ta.teamRole === 'leader'
                                  ? 'Leader — click to make member'
                                  : 'Member — click to make leader'
                              }
                              onClick={() => onToggleTeamRole(ta.teamId)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: rem(3),
                                padding: `${rem(2)} var(--space-2)`,
                                border: 'none',
                                background:
                                  ta.teamRole === 'leader'
                                    ? 'color-mix(in srgb, var(--ac) 14%, transparent)'
                                    : 'var(--sa)',
                                color: ta.teamRole === 'leader' ? 'var(--ac)' : 'var(--tm)',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 700,
                                cursor: 'pointer',
                                textTransform: 'capitalize',
                              }}
                            >
                              <Icon
                                icon={Star}
                                em={0.9091}
                                fill={ta.teamRole === 'leader' ? 'currentColor' : 'none'}
                                aria-hidden="true"
                              />
                              {ta.teamRole}
                            </button>
                          </div>
                        )}
                        {canEditTeams && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-danger"
                            onClick={() => onRemoveTeam(ta.teamId)}
                            title="Remove from team"
                            aria-label={`Remove from ${t?.name || 'team'}`}
                          >
                            <Icon icon={X} em={1.0769} />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {canEditTeams && (
                <SearchableSelect
                  value=""
                  options={availableTeams.map((t) => ({ value: t.id, label: t.name }))}
                  onChange={(v) => v && onAddTeam(v)}
                  placeholder={
                    availableTeams.length === 0 ? 'Already on every team' : 'Add to a team…'
                  }
                  searchPlaceholder="Search teams…"
                  clearable={false}
                  disabled={availableTeams.length === 0}
                />
              )}
            </>
          )}
        </div>

        {/* Project Assignments */}
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 'var(--space-2)',
            }}
          >
            <div style={sectionHeading}>Project Assignments</div>
            {member.id && (
              <Button
                variant={addingProject ? 'secondary' : 'default'}
                size="sm"
                onClick={onToggleAddProject}
              >
                {addingProject ? 'Cancel' : '+ Assign'}
              </Button>
            )}
          </div>

          {!member.id ? (
            <div style={emptyPill}>Save member first to assign projects.</div>
          ) : (
            <>
              {addingProject && (
                <div style={{ marginBottom: 'var(--space-2)' }}>
                  <SearchableSelect
                    value=""
                    options={availableProjects.map((p) => ({
                      value: p.id,
                      label: p.name,
                      hint: p.projectNumber || undefined,
                    }))}
                    onChange={(v) => v && onAssignProject(v)}
                    placeholder={
                      availableProjects.length === 0
                        ? 'All projects already assigned'
                        : 'Search projects to assign…'
                    }
                    searchPlaceholder="Search projects…"
                    clearable={false}
                    disabled={availableProjects.length === 0}
                    autoOpenOnFocus
                  />
                </div>
              )}

              {memberProjectAssignments.length === 0 ? (
                <div style={emptyPill}>No projects assigned yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: rem(3) }}>
                  {memberProjectAssignments.map((a) => {
                    const p = projectById(a.projectId);
                    return (
                      <div
                        key={a.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          padding: `${rem(7)} ${rem(10)}`,
                          background: 'var(--sf)',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid var(--bd)',
                        }}
                      >
                        {p?.projectNumber && (
                          <span
                            style={{
                              fontFamily: "'JetBrains Mono', monospace",
                              color: 'var(--ac)',
                              fontWeight: 700,
                              fontSize: 'var(--font-size-sm)',
                              flexShrink: 0,
                              minWidth: rem(55),
                            }}
                          >
                            {p.projectNumber}
                          </span>
                        )}
                        <span
                          style={{ flex: 1, fontSize: 'var(--font-size-base)', fontWeight: 500 }}
                        >
                          {p?.name || a.projectId}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-danger"
                          onClick={() => onUnassignProject(a.id)}
                          title="Remove"
                          aria-label="Remove"
                        >
                          <Icon icon={X} em={1.0769} />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
